import {DateTime} from "luxon";
import {BehaviorSubject} from "rxjs";
import {inject, singleton} from "tsyringe";
import {ChangeTracker} from "../../change-tracking/change-tracker";
import {appState} from "../../app.state";
import {CreateKnowledgeObjectCommand} from "../../commands/create-knowledge-object.command";
import {DeleteKnowledgeObjectCommand} from "../../commands/delete-knowledge-object.command";
import {LOCKABLE_ENTITY_TYPE_KNOWLEDGE_OBJECT} from "../../commands/lockable-entity-type.constant";
import {LockEntityCommand} from "../../commands/lock-entity.command";
import {OverrideEntityLockCommand} from "../../commands/override-entity-lock.command";
import {ReleaseEntityLockCommand} from "../../commands/release-entity-lock.command";
import {SaveKnowledgeObjectContentCommand} from "../../commands/save-knowledge-object-content.command";
import {BackendClient} from "../../connectivity/backend-client";
import {LockConflictError} from "../../connectivity/lock-conflict.error";
import {QueryParameter} from "../../connectivity/query-parameter.interface";
import {persistence} from "../../decorators/persistence.decorator";
import {TransformerHelper} from "../../helpers/transformer.helper";
import {MessageBroker} from "../../message-broker/message-broker";
import {EntityChangedPayload} from "../../message-broker/payloads/entity-changed.payload";
import {EntityCreatedPayload} from "../../message-broker/payloads/entity-created.payload";
import {ChangeSet} from "../entities/change-set.interface";
import {EntityState} from "../../persistence/entity-state.enum";
import {Repository} from "../../persistence/repository";
import {CommandBus} from "../../services/command-bus.service";
import {KnowledgeFiltersDTO} from "../dtos/knowledge-filters.dto";
import {KnowledgeObjectDTO} from "../dtos/knowledge-object.dto";
import {EntityObjectArray} from "../entity-object-array";
import {KnowledgeObject} from "../entities/knowledge-object.entity";
import {KnowledgeObjectType} from "../entities/knowledge-object-type.type";

/**
 * Frontend repository for KnowledgeObject metadata (PRD-066).
 *
 * Holds a live `items$` stream that is refreshed exclusively via `reload()`
 * — there is no polling. The view owns an `EntityLiveRefreshController` that
 * calls `reload()` whenever an `entity.*` MessageBroker event for
 * KnowledgeObject fires. The repository self-publishes `entity.created` after
 * a successful create, since the backend has no event infrastructure.
 */
@singleton()
@persistence({entity: KnowledgeObject})
export class KnowledgeObjectRepository extends Repository<KnowledgeObject> {

    /** Live list of all (non-deleted) KnowledgeObjects, updated by `reload()`. */
    public readonly items$ = new BehaviorSubject<KnowledgeObject[]>([]);

    /** Monotonic counter to discard out-of-order `reload()` responses (race guard). */
    private _reloadGeneration = 0;

    public constructor(
        @inject(BackendClient) private readonly _backendClient: BackendClient,
        @inject(CommandBus) private readonly _commandBus: CommandBus,
        @inject(MessageBroker) private readonly _messageBroker: MessageBroker,
    ) {
        super();
    }

    /**
     * Fetches all non-deleted KnowledgeObjects. The query intentionally
     * requests NO `includes` — content must never be loaded in the list view.
     *
     * Optional `filters` narrow the result set:
     *  - `type`: exact match (`type eq value`)
     *  - `titleSearch`: case-insensitive substring match (`title like %value%`)
     *    — applied only when the trimmed length is >= 3.
     *
     * Not an override of Repository.findAll: that one takes includes and paging, and the
     * comboboxes call it polymorphically with that signature.
     */
    public async findAllFiltered(filters?: Partial<KnowledgeFiltersDTO>): Promise<EntityObjectArray<KnowledgeObject>> {
        const queryParams: QueryParameter<KnowledgeObject>[] = [{
            key: `deletedAt`,
            comparator: `eq`,
            value: null,
        }];

        if (filters?.type) {
            queryParams.push({key: `type`, comparator: `eq`, value: filters.type});
        }

        const titleSearch = filters?.titleSearch?.trim() ?? ``;
        if (titleSearch.length >= 3) {
            queryParams.push({key: `title`, comparator: `like`, value: titleSearch});
        }

        const dtos = await this._backendClient.post<KnowledgeObjectDTO[]>(
            `/api/v1/query/knowledge-object`,
            false,
            {queryParams}
        );

        const entities: KnowledgeObject[] = [];
        for (const dto of dtos ?? []) {
            entities.push(await TransformerHelper.fromDTO(dto, KnowledgeObject));
        }

        return new EntityObjectArray<KnowledgeObject>(entities.length, entities);
    }

    /**
     * The uid of the image KnowledgeObject that already holds these bytes, or
     * null when none does.
     *
     * Uploads are deduplicated by checksum, so pasting the same picture twice
     * returns the same asset — but a second image object for it would split the
     * picture's references across two identities, because what gets embedded
     * and counted is the object, not the bytes. The paste path asks this before
     * creating anything.
     *
     * A miss is the normal answer for a picture pasted the first time; it is not
     * an error and must not be reported as one.
     */
    public async findImageObjectUidByAsset(assetUid: string): Promise<string | null> {
        const result = await this._backendClient.post<{ uid: string | null }>(
            `/api/v1/query/knowledge-object-by-asset`,
            false,
            {assetSid: assetUid}
        );

        return result?.uid ?? null;
    }

    /**
     * Fetches a single KnowledgeObject by UID. Like `findAllFiltered()`, requests no
     * `includes` — content loading is deferred to PRD-067.
     */
    public async findOneById(uid: string): Promise<KnowledgeObject | null> {
        const dtos = await this._backendClient.post<KnowledgeObjectDTO[]>(
            `/api/v1/query/knowledge-object`,
            false,
            {queryParams: [{key: `uid`, comparator: `eq`, value: uid}]}
        );

        const dto = (dtos ?? [])[0];

        return dto ? TransformerHelper.fromDTO<KnowledgeObject>(dto, KnowledgeObject) : null;
    }

    /**
     * Fetches a single KnowledgeObject FRESH, for an object the view shows although it is not in
     * `items$` (filtered out by the sidebar's filters, feature plan 0069). `findOneById` alone would hand
     * back the cached instance, because `fromDTO` short-circuits on a loaded uid — so the uid is evicted
     * first: the lookup as in `_emitLocalHolder`, the eviction as in `reload()`. `null` when the object is
     * gone (the server's soft-delete filter leaves out the trash).
     */
    public async refetchOne(uid: string): Promise<KnowledgeObject | null> {
        if (this.entityManager.isLoaded(uid)) {
            const cached = this.entityManager.getLoadedEntity(uid);
            if (cached) {
                this.entityManager.unloadEntity(cached);
            }
        }
        return this.findOneById(uid);
    }

    /**
     * Fetches a single KnowledgeObject by UID together with its persisted
     * whiteboard content (`includes: ['content']`). Use this only when the
     * caller actually needs the content payload — list/overview queries must
     * NOT include content (see Failure Condition 5 in PRD-076).
     */
    public async findOneByIdWithContent(uid: string): Promise<KnowledgeObject | null> {
        const dtos = await this._backendClient.post<KnowledgeObjectDTO[]>(
            `/api/v1/query/knowledge-object`,
            false,
            {
                queryParams: [{key: `uid`, comparator: `eq`, value: uid}],
                includes: [`content`],
            }
        );

        const dto = (dtos ?? [])[0];
        if (!dto) {
            return null;
        }

        const entity = await TransformerHelper.fromDTO<KnowledgeObject>(dto, KnowledgeObject);

        // EntityManager caches transformed entities by uid. The first load (list
        // query without `includes:['content']`) wins and any subsequent transform
        // returns the cached instance unchanged — the transformer's `toEntity`
        // (which would set `entity.content`) is short-circuited. Merge the
        // optional content here so a follow-up `includes:['content']` query
        // actually hydrates the entity.
        if (entity && dto.content) {
            entity.content = {payload: dto.content.payload};
        }

        // Same cache-short-circuit reasoning applies to the lock fields: a
        // cached entity from an earlier list load could be showing a STALE
        // `lockedBy` (someone else may have acquired/released the lock since)
        // — always refresh it from this fresh read, the one a caller uses
        // right before deciding whether to enter edit mode.
        if (entity) {
            entity.lockedAt = dto.lockedAt ?? null;
            entity.lockedBy = dto.lockedBy ?? null;
            // `updatedAt` is the baseline a local draft is compared against
            // (whiteboard `baselineUpdatedAt`) — a cached instance kept the
            // value of its FIRST load, so after a save (by this user or
            // another) every draft looked either falsely stale or falsely
            // current. Review 2026-09-19, F2.
            if (dto.updatedAt) {
                entity.updatedAt = DateTime.fromSQL(dto.updatedAt);
            }
        }

        return entity;
    }

    /**
     * Creates a new KnowledgeObject via `CreateKnowledgeObjectCommand`. On a
     * successful HTTP response, self-publishes `entity.created` to the Frontend
     * MessageBroker so subscribed `EntityLiveRefreshController`s reload.
     * Returns the created entity — its UID is client-generated and known here.
     */
    public async create(
        title: string = `Neues Whiteboard`,
        type: KnowledgeObjectType = `whiteboard`,
    ): Promise<KnowledgeObject> {
        const knowledgeObject = ChangeTracker.create(new KnowledgeObject());
        knowledgeObject.type = type;
        knowledgeObject.title = title;

        this.entityManager.setLoadedEntity(knowledgeObject, EntityState.NEW);

        const success = await this._commandBus.dispatchCommand(
            new CreateKnowledgeObjectCommand(knowledgeObject)
        );
        if (!success) {
            throw new Error(`Failed to create KnowledgeObject «${knowledgeObject.uid}».`);
        }

        knowledgeObject.markAsPersisted();
        this._publishCreated(knowledgeObject);

        return knowledgeObject;
    }

    /**
     * Persists the canvas payload of a KnowledgeObject's content via
     * `SaveKnowledgeObjectContentCommand`. On a successful HTTP response,
     * self-publishes `entity.changed` so subscribed
     * `EntityLiveRefreshController`s can reload (PRD-076 Failure Condition 7).
     *
     * READ VIA `dispatchCommandForResult`, not the boolean `dispatchCommand`
     * (Stefan, 2026-09-15, same reasoning as `BackendLinkviewRepository
     * ._runSave`): a plain boolean cannot distinguish "the lock was lost
     * mid-edit" (409, `LockConflictError` — `SaveKnowledgeObjectContentCommandHandler`
     * now guards the write against the object's OWN lock, mirroring
     * `UpdateLinkTileCommandHandler`'s guard on its parent page's lock) from
     * "the server broke" — the caller needs to tell those apart to show the
     * right message and NOT silently discard the user's edits as a generic
     * failure. `LockConflictError` is re-thrown BY IDENTITY, same as the
     * Linkview repository's own `_runLockCommand`, so a caller's
     * `instanceof` check keeps working.
     */
    public async saveContent(uid: string, payload: readonly unknown[]): Promise<void> {
        const result = await this._commandBus.dispatchCommandForResult(
            new SaveKnowledgeObjectContentCommand({objectUid: uid, payload})
        );
        if (!result.success) {
            if (result.error instanceof LockConflictError) {
                // The lock was taken over by somebody else WHILE this save was
                // in flight — refresh so the caller's conflict handler already
                // sees the current holder, same requirement as `_runLockCommand`.
                await this._refreshLockState(uid);
            }
            throw result.error instanceof Error
                ? result.error
                : new Error(`Failed to save content for KnowledgeObject «${uid}».`);
        }

        this._publishChanged(uid);
    }

    /**
     * Takes the edit lock. Mirrors `BackendLinkviewRepository.acquireLock` —
     * same reuse of the mechanism Stefan asked for — but simpler: content
     * isn't diffed against a read baseline the way Linkview tiles are, so
     * there is no "one read on entry" ceremony here, just the lock command
     * itself. The caller (`knowledge-rte`/`knowledge-whiteboard`) already
     * holds the loaded entity and updates its own local `_mode` after this
     * resolves.
     */
    public async acquireLock(uid: string): Promise<void> {
        await this._runLockCommand(uid, new LockEntityCommand({
            entityType: LOCKABLE_ENTITY_TYPE_KNOWLEDGE_OBJECT,
            uid,
        }));
        this._emitLocalHolder(uid, appState.user?.uid ?? null);
    }

    /** Gives the edit lock back. See `acquireLock`'s own doc comment. */
    public async releaseLock(uid: string): Promise<void> {
        await this._runLockCommand(uid, new ReleaseEntityLockCommand({
            entityType: LOCKABLE_ENTITY_TYPE_KNOWLEDGE_OBJECT,
            uid,
        }));
        this._emitLocalHolder(uid, null);
    }

    /**
     * Takes the lock over from somebody else. `expectedHolderUid` goes out
     * VERBATIM — see `OverrideEntityLockCommand`'s own doc comment for why
     * converting it breaks every take-over.
     */
    public async overrideLock(uid: string, expectedHolderUid: string | null): Promise<void> {
        await this._runLockCommand(uid, new OverrideEntityLockCommand({
            entityType: LOCKABLE_ENTITY_TYPE_KNOWLEDGE_OBJECT,
            uid,
            expectedHolderUid,
        }));
        this._emitLocalHolder(uid, appState.user?.uid ?? null);
    }

    /**
     * Sends one lock command and turns its outcome into a resolution or the
     * right KIND of rejection — mirrors `BackendLinkviewRepository
     * ._runLockCommand` exactly (see that method's own doc comment for the
     * two obligations: read via `dispatchCommandForResult` so a
     * `LockConflictError` can be re-thrown BY IDENTITY, and refresh `items$`
     * BEFORE the rejection settles so a caller's conflict handler already
     * sees the current holder).
     */
    private async _runLockCommand(
        uid: string,
        command: LockEntityCommand | ReleaseEntityLockCommand | OverrideEntityLockCommand,
    ): Promise<void> {
        const result = await this._commandBus.dispatchCommandForResult(command);

        if (result.success) {
            return;
        }

        if (result.error instanceof LockConflictError) {
            await this._refreshLockState(uid);
            throw result.error;
        }

        throw result.error instanceof Error
            ? result.error
            : new Error(`KnowledgeObject lock command ${command.name} failed for ${uid}`);
    }

    /**
     * Writes the holder this client just became (or stopped being) into both
     * `items$` (if the object is currently in that list) and the
     * EntityManager-cached entity instance (if loaded) — without a further
     * round-trip, since the server already agreed (the command succeeded).
     * Same "server already agreed, no re-read needed" reasoning as
     * `BackendLinkviewRepository._emitLocalHolder`.
     */
    private _emitLocalHolder(uid: string, holderUid: string | null): void {
        const cached = this.entityManager.isLoaded(uid)
            ? this.entityManager.getLoadedEntity(uid) as KnowledgeObject
            : null;
        if (cached) {
            cached.lockedBy = holderUid;
            cached.lockedAt = holderUid === null ? null : (cached.lockedAt ?? new Date().toISOString());
        }

        if (this.items$.value.some((item) => item.uid === uid)) {
            this.items$.next(this.items$.value.map((item) => {
                if (item.uid !== uid) {
                    return item;
                }
                item.lockedBy = holderUid;
                item.lockedAt = holderUid === null ? null : (item.lockedAt ?? new Date().toISOString());
                return item;
            }));
        }
    }

    /**
     * A rejected lock command PROVES this client's belief about the holder is
     * wrong, so the entity is re-read from the server — same "the row is
     * refreshed BEFORE the rejection settles" requirement as Linkview's own
     * conflict handling, so a caller's catch already finds the CURRENT holder
     * to name in a second warning.
     */
    private async _refreshLockState(uid: string): Promise<void> {
        if (this.entityManager.isLoaded(uid)) {
            await this.findOneByIdWithContent(uid);
        }
    }

    /**
     * Deletes a KnowledgeObject via `DeleteKnowledgeObjectCommand`, giving the
     * delete path the same call-site shape as `create()`/`saveContent()`.
     *
     * Deliberately does NOT self-publish `entity.deleted`: the registered
     * `DeleteKnowledgeObjectCommandHandler` publishes it with the entity
     * INSTANCE (which `EntityLiveRefreshController` matches on via
     * `payload.entity.constructor`), and the generic `command-send.listener`
     * publishes it for every `delete.*` command as well. Throws when the
     * backend rejects the command, so a caller never treats a still-persisted
     * object as deleted.
     */
    public async delete(knowledgeObject: KnowledgeObject): Promise<void> {
        const success = await this._commandBus.dispatchCommand(
            new DeleteKnowledgeObjectCommand(knowledgeObject)
        );
        if (!success) {
            throw new Error(`Failed to delete KnowledgeObject «${knowledgeObject.uid}».`);
        }
    }

    /**
     * Reloads the list from the backend and emits it into `items$`.
     * This is the ONLY mechanism that updates `items$`. Optional `filters`
     * are forwarded to `findAllFiltered()`.
     */
    public async reload(filters?: Partial<KnowledgeFiltersDTO>): Promise<void> {
        // Stale-response guard: reload() is triggered from several sources that can
        // overlap (the view's initial mount load, the knowledgeFilters listener,
        // and the EntityLiveRefreshController). Without this guard two concurrent
        // findAllFiltered() calls race and whichever RESOLVES LAST wins — so a stale/older
        // response can overwrite a newer one, making the (filtered) list flicker
        // and settle on the wrong result. Capture a generation before the await and
        // discard the result if a newer reload has since started.
        const generation = ++this._reloadGeneration;

        // Evict currently-cached entities BEFORE re-fetching: AbstractDtoToEntityTransformer.
        // transform() short-circuits to the cached instance whenever EntityManager.isLoaded(uid)
        // is true — a fresh DTO for an already-loaded item (e.g. after a title rename, or after
        // a whiteboard save via _publishChanged()) would otherwise be silently discarded, leaving
        // items$ (and everything bound to it, incl. the Wissen sidebar list) showing stale data
        // forever. reload() is precisely the "something changed, fetch fresh state" signal, so
        // it must actually apply the fresh values — same mechanism Repository.reloadEntity()
        // already uses for the single-entity case (2026-08-17).
        // Trade-off (review 03, WARNING, Linse B, 2026-08-18): this evicts EVERY
        // currently-cached KnowledgeObject, not just the one whose change actually
        // triggered this reload() — EntityLiveRefreshController calls reload() on
        // every entity.changed for the type, including ones from OTHER users in the
        // same tenant via live collaboration, and reload() itself has no way to know
        // which single uid changed (it's only ever told "something of this type
        // changed"). A targeted eviction would need the changed uid threaded through
        // EntityLiveRefreshController — a shared controller used by other list
        // components too — which is a bigger architectural change than this fix
        // warrants. Currently safe in practice: every live consumer that keeps its own
        // state across a reload (knowledge-whiteboard.component.ts,
        // knowledge-rte.component.ts — incl. its unsaved-draft autosave state — and
        // knowledge-image.component.ts) guards via `item.uid === this._lastItemUid`,
        // not via object identity, so a reload triggered by an unrelated object's
        // change doesn't lose in-progress content. A future regression here would
        // affect ALL THREE of those, not just the whiteboard (review 04, WARNING,
        // Linse B, 2026-08-18 — corrects an earlier version of this comment that
        // named only one of the three).
        for (const item of this.items$.value) {
            this.entityManager.unloadEntity(item);
        }

        const items = await this.findAllFiltered(filters);
        if (generation !== this._reloadGeneration) {
            return;
        }
        this.items$.next([...items]);
    }

    /** Publishes an `entity.created` event for a freshly created KnowledgeObject. */
    private _publishCreated(knowledgeObject: KnowledgeObject): void {
        this._messageBroker.publish<EntityCreatedPayload<KnowledgeObject>>({
            type: `entity.created`,
            identityHash: knowledgeObject.uid,
            payload: {
                uid: knowledgeObject.uid,
                prototype: KnowledgeObject,
                entity: knowledgeObject,
            }
        });
    }

    /**
     * Publishes an `entity.changed` event for the given KnowledgeObject uid.
     * Used by `saveContent()` where no live entity instance is at hand — the
     * changeSet is empty because subscribers (EntityLiveRefreshController)
     * key off `prototype` only and trigger a `reload()`.
     *
     * Deliberately NOT also called from `acquireLock`/`releaseLock`/
     * `overrideLock` (tried in review-01, reverted in review-02): the only
     * current `entity.changed` subscriber for `KnowledgeObject`
     * (`knowledge-sidebar-list.component.ts`) does a full `reload()` —
     * evicting the ENTIRE tenant-wide cache — and has no lock-status UI to
     * actually benefit from it. Wiring lock ops in would have fired that
     * full reload on every "Bearbeiten"/Save/Cancel click across the whole
     * tenant for zero visible gain. Revisit if a lock-status indicator is
     * ever added to that list.
     */
    private _publishChanged(uid: string): void {
        this._messageBroker.publish<EntityChangedPayload<KnowledgeObject>>({
            type: `entity.changed`,
            identityHash: uid,
            payload: {
                uid,
                prototype: KnowledgeObject,
                changeSet: {} as ChangeSet<KnowledgeObject>,
            }
        });
    }
}
